import { languageItemList } from 'components';
import { REPORT_SETTINGS_PERIOD_OPTIONS } from '../consts';
import { ReportDateRange } from '../models/preview/preview.models';
import {
	buildPreviewDateRange,
	platformDisplayName
} from './report-preview-utils';
import { REPORT_CUSTOM_PERIOD, customPeriodLabel } from './report-period';
import {
	hasReportMessage,
	reportDateLocale,
	reportMessage,
	reportMessageLanguage
} from './report-messages';
import {
	ReportBlockCatalogueEntry,
	ReportBlockGroupBy,
	ReportBlockScope,
	ReportBlockSentiment,
	ReportBlockSort,
	ReportBlockSpec
} from './report-blocks.service';

/**
 * A chart's own read: the words the Data control offers it in, and how the
 * source line names what a chart was actually read over.
 *
 * WHICH choices a chart is offered is not decided here. The server's
 * catalogue lists them per block (`options`, from `blocks/overrides.py`) —
 * a heatmap has no top N, a rate no weeks, a breakdown no period beside it —
 * because only the resolver knows what its reads can answer, and a control
 * it would quietly ignore is a control that lies.
 */

/**
 * The two sides of a chart set beside the period before: the backend's own
 * series and figure labels (`reshape.py`), so a tooltip can tell which window
 * one of its numbers came from.
 */
export const CURRENT_PERIOD_LABEL = 'This period';
export const PREVIOUS_PERIOD_LABEL = 'Previous period';

/** Every period a report can have; a chart takes the same keys. */
export const REPORT_BLOCK_PERIODS = REPORT_SETTINGS_PERIOD_OPTIONS;

/** Every platform a mention comes from, in the mentions filter's order. */
export const REPORT_BLOCK_PLATFORMS: readonly string[] = [
	'instagram',
	'tiktok',
	'youtube',
	'x',
	'facebook',
	'linkedin',
	'reddit',
	'web',
	'news',
	'blogs',
	'forums'
];

export const REPORT_BLOCK_SENTIMENTS: ReadonlyArray<{
	value: ReportBlockSentiment;
	label: string;
}> = [
	{ value: 'positive', label: 'Positive' },
	{ value: 'neutral', label: 'Neutral' },
	{ value: 'negative', label: 'Negative' }
];

export const REPORT_BLOCK_GROUPINGS: ReadonlyArray<{
	value: ReportBlockGroupBy;
	label: string;
}> = [
	{ value: 'day', label: 'Day' },
	{ value: 'week', label: 'Week' },
	{ value: 'month', label: 'Month' }
];

export const REPORT_BLOCK_SORTS: ReadonlyArray<{
	value: ReportBlockSort;
	label: string;
}> = [
	{ value: 'largest_first', label: 'Largest first' },
	{ value: 'smallest_first', label: 'Smallest first' },
	{ value: 'alphabetical', label: 'A–Z' }
];

/** The top lists the PRD asks for. */
export const REPORT_BLOCK_TOP_N: readonly number[] = [5, 10, 20];

export function reportPeriodLabel(
	period: string | null | undefined,
	periodFrom?: string | null,
	periodTo?: string | null
): string {
	if (period === REPORT_CUSTOM_PERIOD && periodFrom && periodTo) {
		return customPeriodLabel(periodFrom, periodTo);
	}
	return (
		REPORT_BLOCK_PERIODS.find(option => option.value === period)?.label ??
		(period ?? '').replace(/_/g, ' ')
	);
}

export function languageLabel(code: string): string {
	return (
		languageItemList.find(language => language.id === code)?.name ??
		code.toUpperCase()
	);
}

/** Whether a chart reads anything but the report's own window. */
export function hasOwnRead(spec: ReportBlockSpec | null | undefined): boolean {
	return Boolean(
		spec && (spec.filters || spec.group_by || spec.sort || spec.compare)
	);
}

/**
 * A chart moved to another block of its measure group — "reach" in place of
 * "mentions" over the same days.
 *
 * Everything the new block's read can answer comes along and the rest is
 * dropped, so the spec is one the server accepts. The heading follows the
 * measure unless the reader typed it: a heading about mentions over a chart
 * of reach is a claim the numbers under it contradict, and only the reader's
 * own words are theirs to keep.
 */
export function specForMeasure(
	spec: ReportBlockSpec,
	entry: ReportBlockCatalogueEntry
): ReportBlockSpec {
	const offers = new Set(entry.options ?? []);
	return {
		...spec,
		block_id: entry.id,
		render:
			spec.render && entry.renders.includes(spec.render)
				? spec.render
				: entry.renders[0],
		title: spec.title_edited_by_user ? spec.title : entry.name,
		limit: offers.has('limit') ? spec.limit ?? null : null,
		group_by: offers.has('group_by') ? spec.group_by ?? null : null,
		sort: offers.has('sort') ? spec.sort ?? null : null,
		compare: offers.has('compare') ? spec.compare ?? null : null,
		// A block that is not comparable is still drawn for one entity.
		entities: entry.comparable ? spec.entities : spec.entities?.slice(0, 1)
	};
}

/**
 * A chart Ana rebuilds keeps the read the reader gave it.
 *
 * Ana's chart tool cannot set a chart's own period or filters yet (its schema
 * budget is spent), so a spec from her never carries them — and replacing the
 * chart whole would quietly put the reader's year back to the report's month
 * under whatever heading she was asked to write. The period and the filters
 * apply to every block, so they stay on any replacement. How the answer is
 * laid out belongs to a block's shape, so it stays only on the same block,
 * and the period before only while the chart is about one entity. Everything
 * Ana CAN send is hers to replace, exactly as before.
 */
export function keepReaderDataChoices(
	previous: ReportBlockSpec | undefined,
	next: ReportBlockSpec
): ReportBlockSpec {
	if (!previous || !hasOwnRead(previous)) {
		return next;
	}
	const kept: ReportBlockSpec = { ...next };
	if (previous.filters && next.filters === undefined) {
		kept.filters = previous.filters;
	}
	if (previous.block_id === next.block_id) {
		if (previous.group_by && next.group_by === undefined) {
			kept.group_by = previous.group_by;
		}
		if (previous.sort && next.sort === undefined) {
			kept.sort = previous.sort;
		}
		if (
			previous.compare &&
			next.compare === undefined &&
			(next.entities?.length ?? 0) <= 1
		) {
			kept.compare = previous.compare;
		}
	}
	return kept;
}

/**
 * Why a chart could not be read again with its new data, as a toast says it.
 *
 * The server's own sentence when it refused the spec — a 422 names what the
 * chart's read cannot answer — and plain words when the read failed for any
 * other reason. Either way the chart is as it was: the new spec never reached
 * the page.
 */
export function whyNotRedrawn(error: unknown): string {
	const detail = (error as { error?: { detail?: unknown } } | null)?.error
		?.detail;
	const first = Array.isArray(detail)
		? (detail[0] as { msg?: unknown } | undefined)?.msg
		: detail;
	const reason =
		typeof first === 'string'
			? first
					.replace(/^Value error, /, '')
					.trim()
					.replace(/\.$/, '')
			: '';
	return reason
		? `That chart could not be redrawn: ${reason}. It is unchanged.`
		: 'That chart could not be redrawn just now, so it is unchanged.';
}

/** A chart's own read, as a phrase: "the last year, TikTok only, by week". */
export function describeOwnRead(spec: ReportBlockSpec): string | null {
	const parts: string[] = [];
	const filters = spec.filters;
	if (filters?.period) {
		parts.push(reportPeriodLabel(filters.period).toLowerCase());
	}
	if (filters?.platforms?.length) {
		parts.push(filters.platforms.map(platformDisplayName).join(', '));
	}
	if (filters?.sentiments?.length) {
		parts.push(`${filters.sentiments.join(', ')} mentions`);
	}
	if (filters?.languages?.length) {
		parts.push(filters.languages.map(languageLabel).join(', '));
	}
	if (spec.group_by && spec.group_by !== 'day') {
		parts.push(`by ${spec.group_by}`);
	}
	if (spec.compare === 'previous_period') {
		parts.push('beside the previous period');
	}
	return parts.length ? parts.join('; ') : null;
}

// ── what a chart was read over ────────────────────────────────────────────────

/** A filter the chart was read with, named the way a reader names it. */
export interface ReportScopeFilter {
	name: string;
	value: string;
}

/**
 * The window a chart with a read of its own was read over, as the source line
 * takes one. `fallback` is the report's own window, which a chart whose read
 * changes only its filters shares.
 */
export function scopeRange(
	scope: ReportBlockScope,
	fallback: ReportDateRange
): ReportDateRange {
	const period = scope.period ?? fallback.period;
	if (!scope.to_date) {
		return fallback;
	}
	if (!scope.from_date) {
		// All time: the preview's own range for it, so the line says so.
		return buildPreviewDateRange('all_time', scope.to_date);
	}
	// No label: it is shown only for instants that do not parse, and the
	// report's own would name a window this chart was not read over.
	return {
		period,
		start: scope.from_date,
		end: scope.to_date,
		label: ''
	};
}

export function scopePreviousRange(
	scope: ReportBlockScope
): ReportDateRange | null {
	if (!scope.previous_from_date || !scope.previous_to_date) {
		return null;
	}
	return {
		start: scope.previous_from_date,
		end: scope.previous_to_date,
		label: ''
	};
}

export function scopeFilters(
	scope: ReportBlockScope,
	language?: string | null
): ReportScopeFilter[] {
	const filters: ReportScopeFilter[] = [];
	if (scope.platforms?.length) {
		filters.push({
			name: reportMessage(language, 'source.filter.platforms'),
			value: scope.platforms.map(platformDisplayName).join(', ')
		});
	}
	if (scope.sentiments?.length) {
		filters.push({
			name: reportMessage(language, 'source.filter.sentiment'),
			value: scope.sentiments
				.map(sentiment => {
					const key = `source.sentiment.${sentiment}`;
					return hasReportMessage(key)
						? reportMessage(language, key)
						: sentiment;
				})
				.join(', ')
		});
	}
	if (scope.languages?.length) {
		filters.push({
			name: reportMessage(language, 'source.filter.languages'),
			value: scope.languages
				.map(code => languageName(code, language))
				.join(', ')
		});
	}
	return filters;
}

function languageName(
	code: string,
	language: string | null | undefined
): string {
	if (reportMessageLanguage(language) === 'en') {
		return languageLabel(code);
	}
	try {
		const names = new Intl.DisplayNames([reportDateLocale(language)], {
			type: 'language'
		});
		return names.of(code) ?? languageLabel(code);
	} catch {
		return languageLabel(code);
	}
}

export function scopeGrouping(
	scope: ReportBlockScope,
	language?: string | null
): string | null {
	if (scope.group_by === 'week') {
		return reportMessage(language, 'source.grouping.week');
	}
	if (scope.group_by === 'month') {
		return reportMessage(language, 'source.grouping.month');
	}
	return null;
}
